import React, {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
  type RefObject,
} from "react";
import { Tooltip } from "reactstrap";
import * as Scrivito from "scrivito";
import { SORTING_CRITERIA, tableColumnsWidths } from "variables";
import { VirtualizedModalTable } from "components/VirtualizedTable/VirtualizedModalTable";
import { useTranslate } from "hooks";
import { ProductListTableRow } from "./ProductListTableRow";
import { SortButton } from "../../SortButton/SortButton";
import { useIsSameProj } from "stores/globalStore";
import type { ProductListTableProduct } from "interfaces/product/product.types";

type ProductListTableProps = {
  products: ProductListTableProduct[];
  isOrderDescending: boolean;
  setIsOrderDescending: Dispatch<SetStateAction<boolean>>;
  skeletonCount: number;
  fetchingProductList: boolean;
  fetchingPrices?: boolean;
  tableRef: RefObject<HTMLDivElement | null>;
  isLastPage: boolean;
  setSortCriteria: Dispatch<SetStateAction<string>>;
  setCurrentPageNo: Dispatch<SetStateAction<number>>;
  sortCriteria: (typeof SORTING_CRITERIA)[keyof typeof SORTING_CRITERIA];
  isModalView: boolean;
  priceGroupDescription?: string;
};

export const ProductListTable = Scrivito.connect(
  ({
    products = [],
    isOrderDescending,
    setIsOrderDescending,
    skeletonCount,
    fetchingProductList,
    fetchingPrices = false,
    tableRef,
    isLastPage,
    setSortCriteria,
    sortCriteria,
    setCurrentPageNo,
    isModalView,
    priceGroupDescription,
  }: ProductListTableProps) => {
    const translate = useTranslate();
    const priceColRef = useRef<HTMLElement>(null);
    const isSameProj = useIsSameProj();
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const toggleTooltip = useCallback(
      () => setTooltipOpen(!tooltipOpen),
      [tooltipOpen],
    );

    const unitPriceLabel = !isSameProj
      ? translate("UNIT_PRICE")
      : translate("UNIT_PRICE_SAME");

    const totalPriceLabel = !isSameProj
      ? translate("TOTAL_PRICE")
      : translate("TOTAL_PRICE_SAME");

    const columns = [
      {
        label: (
          <div
            data-testid="ProductListTable__name"
            className="d-flex justify-content-between"
          >
            {translate("NAME")}
            <SortButton
              sortCriteria={SORTING_CRITERIA.NAME}
              currentSort={sortCriteria}
              setSortCriteria={setSortCriteria}
              isOrderDescending={isOrderDescending}
              setIsOrderDescending={setIsOrderDescending}
              setCurrentPageNo={setCurrentPageNo}
            />
          </div>
        ),
        width: tableColumnsWidths.nameWidth,
      },
      {
        label: (
          <div
            data-testid="ProductListTable__presentation"
            className="d-flex justify-content-between"
          >
            {translate("PRESENTATION_ORDER_CODE")}
            <SortButton
              sortCriteria={SORTING_CRITERIA.PRESENTATION}
              currentSort={sortCriteria}
              setSortCriteria={setSortCriteria}
              isOrderDescending={isOrderDescending}
              setIsOrderDescending={setIsOrderDescending}
              setCurrentPageNo={setCurrentPageNo}
            />
          </div>
        ),
        width: tableColumnsWidths.orderCodeWidth,
      },
      {
        label: (
          <div data-testid="ProductListTable__quantity">
            {translate("QUANTITY")}
          </div>
        ),
        width: tableColumnsWidths.quantityWidth,
      },
      {
        label: (
          <div
            data-testid="ProductListTable__unit-price"
            className="d-flex justify-content-between"
          >
            {unitPriceLabel}
            <SortButton
              sortCriteria={SORTING_CRITERIA.UNIT_PRICE}
              currentSort={sortCriteria}
              setSortCriteria={setSortCriteria}
              isOrderDescending={isOrderDescending}
              setIsOrderDescending={setIsOrderDescending}
              setCurrentPageNo={setCurrentPageNo}
            />
          </div>
        ),
        width: tableColumnsWidths.unitPriceWidth,
      },
      {
        label: (
          <div
            data-testid="ProductListTable__total-price"
            className="d-flex justify-content-between"
          >
            {totalPriceLabel}
            <SortButton
              sortCriteria={SORTING_CRITERIA.TOTAL_PRICE}
              currentSort={sortCriteria}
              setSortCriteria={setSortCriteria}
              isOrderDescending={isOrderDescending}
              setIsOrderDescending={setIsOrderDescending}
              setCurrentPageNo={setCurrentPageNo}
            />
          </div>
        ),
        width: tableColumnsWidths.totalPriceWidth,
      },

      {
        label: (
          <>
            <div
              data-testid="ProductListTable__price-group"
              className="d-flex justify-content-between"
            >
              <span ref={priceColRef}>{translate("PRICE_GROUP")}</span>
              <SortButton
                sortCriteria={SORTING_CRITERIA.PRICE_GROUP}
                currentSort={sortCriteria}
                setSortCriteria={setSortCriteria}
                isOrderDescending={isOrderDescending}
                setIsOrderDescending={setIsOrderDescending}
                setCurrentPageNo={setCurrentPageNo}
              />
            </div>

            <Tooltip
              target={priceColRef as RefObject<HTMLElement>}
              placement="top"
              isOpen={tooltipOpen}
              toggle={toggleTooltip}
            >
              <span className="fw-normal text-gray-400">
                {priceGroupDescription}
              </span>
            </Tooltip>
          </>
        ),
        width: tableColumnsWidths.priceGroupWidth,
      },
    ];

    const renderRow = (product: ProductListTableProduct) => (
      <ProductListTableRow
        key={product.id}
        product={product}
        fetchingPrices={fetchingPrices}
      />
    );

    return (
      <VirtualizedModalTable
        columns={columns}
        products={products}
        renderRow={renderRow}
        fetchingProductList={fetchingProductList}
        skeletonCount={skeletonCount}
        isLastPage={isLastPage}
        tableRef={tableRef}
        isModalView={isModalView}
      />
    );
  },
);
